import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import axios from "axios";
import { io } from "socket.io-client";
import Navbar from "@/components/Navbar";
import { trackGameStarted } from "@/utils/analytics";
import { 
  Play, 
  Users, 
  Trophy, 
  Timer, 
  ArrowRight,
  Copy,
  CheckCircle2,
  XCircle,
  Loader2,
  SkipForward,
  StopCircle,
  BarChart3,
  Zap
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function GameHostPage({ user, onLogout }) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  
  // State
  const [modules, setModules] = useState([]);
  const [selectedModule, setSelectedModule] = useState(null);
  const [socket, setSocket] = useState(null);
  const [gameState, setGameState] = useState("idle"); // idle, waiting, active, results, ended
  const [sessionCode, setSessionCode] = useState(null);
  const [players, setPlayers] = useState([]);
  const [currentQuestion, setCurrentQuestion] = useState(null);
  const [questionNumber, setQuestionNumber] = useState(0);
  const [totalQuestions, setTotalQuestions] = useState(0);
  const [responseCount, setResponseCount] = useState(0);
  const [leaderboard, setLeaderboard] = useState([]);
  const [answerDistribution, setAnswerDistribution] = useState({});
  const [showResults, setShowResults] = useState(false);
  const [copied, setCopied] = useState(false);
  const [loading, setLoading] = useState(true);
  const [joinQrCode, setJoinQrCode] = useState(null);

  // Fetch game modules on mount
  useEffect(() => {
    fetchModules();
  }, []);

  // Fetch QR code when session code is set
  useEffect(() => {
    if (sessionCode) {
      fetchJoinQrCode(sessionCode);
    }
  }, [sessionCode]);

  const fetchJoinQrCode = async (code) => {
    try {
      const response = await axios.get(`${API}/game/join-qr/${code}`);
      setJoinQrCode(response.data.qr_code);
    } catch (error) {
      console.error("Failed to fetch QR code:", error);
    }
  };

  // Initialize Socket.IO connection
  useEffect(() => {
    const newSocket = io(BACKEND_URL, {
      transports: ["websocket", "polling"],
      path: "/api/socket.io"
    });

    newSocket.on("connect", () => {
      console.log("[HOST] Connected to game server");
    });

    newSocket.on("disconnect", () => {
      console.log("[HOST] Disconnected from game server");
    });

    // Game created event
    newSocket.on("game:created", (data) => {
      console.log("[HOST] Game created:", data);
      setSessionCode(data.code);
      setTotalQuestions(data.module?.questions?.length || 0);
      setGameState("waiting");
    });

    // Player joined event
    newSocket.on("player:joined", (data) => {
      console.log("[HOST] Player joined:", data);
      setPlayers(data.players || []);
    });

    // Player left event
    newSocket.on("player:left", (data) => {
      console.log("[HOST] Player left:", data);
      setPlayers(prev => prev.filter((_, i) => i < data.player_count));
    });

    // Game started event
    newSocket.on("game:started", (data) => {
      console.log("[HOST] Game started:", data);
      setTotalQuestions(data.total_questions);
      setGameState("active");
      trackGameStarted({ role: user?.role, moduleName: selectedModule?.title, gameMode: "live" });
    });

    // Question shown event
    newSocket.on("question:show", (data) => {
      console.log("[HOST] Question shown:", data);
      setCurrentQuestion(data.question);
      setQuestionNumber(data.question_number);
      setResponseCount(0);
      setShowResults(false);
      setAnswerDistribution({});
    });

    // Answer received event
    newSocket.on("answer:received", (data) => {
      console.log("[HOST] Answer received:", data);
      setResponseCount(data.response_count);
    });

    // Results shown event
    newSocket.on("results:show", (data) => {
      console.log("[HOST] Results:", data);
      setLeaderboard(data.leaderboard || []);
      setAnswerDistribution(data.answer_distribution || {});
      setShowResults(true);
    });

    // Game ended event
    newSocket.on("game:ended", (data) => {
      console.log("[HOST] Game ended:", data);
      setLeaderboard(data.final_leaderboard || []);
      setGameState("ended");
    });

    setSocket(newSocket);

    return () => {
      newSocket.disconnect();
    };
  }, []);

  const fetchModules = async () => {
    try {
      const response = await axios.get(`${API}/game/modules`);
      setModules(response.data);
    } catch (error) {
      console.error("Failed to fetch modules:", error);
    } finally {
      setLoading(false);
    }
  };

  const createGame = () => {
    if (!socket || !selectedModule) return;
    
    socket.emit("host_create_game", {
      host_id: user.id,
      module_id: selectedModule.id,
      settings: {
        time_limit_multiplier: 1.0,
        team_mode: false,
        show_leaderboard: true,
        max_players: 50
      }
    });
  };

  const startGame = () => {
    if (!socket || !sessionCode) return;
    socket.emit("host_start_game", { code: sessionCode });
  };

  const nextQuestion = () => {
    if (!socket || !sessionCode) return;
    setShowResults(false);
    socket.emit("host_next_question", { code: sessionCode });
  };

  const showQuestionResults = () => {
    if (!socket || !sessionCode) return;
    socket.emit("host_show_results", { code: sessionCode });
  };

  const endGame = () => {
    if (!socket || !sessionCode) return;
    socket.emit("host_end_game", { code: sessionCode });
  };

  const copyCode = () => {
    navigator.clipboard.writeText(sessionCode);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const resetGame = () => {
    setGameState("idle");
    setSessionCode(null);
    setPlayers([]);
    setCurrentQuestion(null);
    setQuestionNumber(0);
    setResponseCount(0);
    setLeaderboard([]);
    setShowResults(false);
    setSelectedModule(null);
  };

  // Module selection screen
  const renderModuleSelection = () => (
    <div className="max-w-4xl mx-auto">
      <div className="text-center mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">🎮 Launch Live Game</h1>
        <p className="text-gray-600">Select a game module to start a classroom session</p>
        <button
          onClick={() => navigate("/game/demo")}
          className="mt-4 text-purple-600 hover:text-purple-700 text-sm font-medium inline-flex items-center gap-1"
        >
          <Zap className="w-4 h-4" />
          Need a quick demo for presentations? Try Demo Mode →
        </button>
      </div>

      {loading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-yellow-500" />
        </div>
      ) : (
        <div className="grid md:grid-cols-3 gap-4 mb-8">
          {modules.map((module) => (
            <button
              key={module.id}
              onClick={() => setSelectedModule(module)}
              data-testid={`module-select-${module.id}`}
              className={`p-6 rounded-xl border-2 transition-all text-left ${
                selectedModule?.id === module.id
                  ? "border-yellow-500 bg-yellow-50"
                  : "border-gray-200 hover:border-yellow-300 bg-white"
              }`}
            >
              <div className="text-3xl mb-3">
                {module.session_number === 1 && "💡"}
                {module.session_number === 2 && "🦅"}
                {module.session_number === 3 && "📋"}
              </div>
              <h3 className="font-bold text-gray-900 mb-1">{module.title}</h3>
              <p className="text-sm text-gray-500 mb-2">{module.description}</p>
              <div className="text-xs text-gray-400">
                {module.question_count} questions
              </div>
            </button>
          ))}
        </div>
      )}

      <div className="text-center">
        <button
          onClick={createGame}
          disabled={!selectedModule}
          data-testid="create-game-btn"
          className="bg-yellow-500 hover:bg-yellow-600 disabled:bg-gray-300 text-white px-8 py-4 rounded-full font-bold text-lg transition-all inline-flex items-center gap-2"
        >
          <Play className="w-5 h-5" />
          Create Game Session
        </button>
      </div>
    </div>
  );

  // Waiting room screen
  const renderWaitingRoom = () => (
    <div className="max-w-4xl mx-auto text-center">
      <div className="bg-gradient-to-br from-yellow-400 to-orange-500 text-white rounded-3xl p-8 mb-8">
        <div className="grid md:grid-cols-2 gap-8 items-center">
          {/* Code Section */}
          <div>
            <h1 className="text-xl font-bold mb-2">Join at</h1>
            <div className="text-sm opacity-90 mb-2">
              {window.location.origin}/game/join
            </div>
            <div className="text-5xl font-mono font-bold tracking-wider mb-4">
              {sessionCode}
            </div>
            <button
              onClick={copyCode}
              className="bg-white/20 hover:bg-white/30 px-6 py-2 rounded-full inline-flex items-center gap-2 transition-all"
            >
              {copied ? <CheckCircle2 className="w-5 h-5" /> : <Copy className="w-5 h-5" />}
              {copied ? "Copied!" : "Copy Code"}
            </button>
          </div>
          
          {/* QR Code Section */}
          {joinQrCode && (
            <div className="flex flex-col items-center">
              <p className="text-sm opacity-90 mb-2">Or scan to join</p>
              <div className="bg-white p-3 rounded-xl">
                <img 
                  src={joinQrCode} 
                  alt="Join QR Code"
                  className="w-40 h-40"
                />
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="bg-white rounded-2xl p-8 shadow-lg mb-8">
        <div className="flex items-center justify-center gap-2 mb-4">
          <Users className="w-6 h-6 text-yellow-500" />
          <span className="text-2xl font-bold">{players.length} Players</span>
        </div>
        
        {players.length === 0 ? (
          <p className="text-gray-500">Waiting for players to join...</p>
        ) : (
          <div className="flex flex-wrap justify-center gap-2">
            {players.map((player, idx) => (
              <span
                key={idx}
                className="bg-yellow-100 text-yellow-800 px-3 py-1 rounded-full text-sm font-medium"
              >
                {player.nickname}
              </span>
            ))}
          </div>
        )}
      </div>

      <div className="flex gap-4 justify-center">
        <button
          onClick={resetGame}
          className="bg-gray-200 hover:bg-gray-300 text-gray-700 px-6 py-3 rounded-full font-semibold transition-all"
        >
          Cancel
        </button>
        <button
          onClick={startGame}
          disabled={players.length === 0}
          data-testid="start-game-btn"
          className="bg-green-500 hover:bg-green-600 disabled:bg-gray-300 text-white px-8 py-3 rounded-full font-bold text-lg transition-all inline-flex items-center gap-2"
        >
          <Play className="w-5 h-5" />
          Start Game ({players.length} players)
        </button>
      </div>
    </div>
  );

  // Active question screen
  const renderActiveQuestion = () => {
    if (!currentQuestion) {
      return (
        <div className="max-w-4xl mx-auto text-center py-12">
          <Loader2 className="w-12 h-12 animate-spin text-yellow-500 mx-auto mb-4" />
          <p className="text-gray-600">Loading question...</p>
          <button
            onClick={nextQuestion}
            data-testid="first-question-btn"
            className="mt-8 bg-yellow-500 hover:bg-yellow-600 text-white px-8 py-4 rounded-full font-bold text-lg transition-all inline-flex items-center gap-2"
          >
            <Play className="w-5 h-5" />
            Show First Question
          </button>
        </div>
      );
    }

    const optionColors = {
      A: "bg-red-500",
      B: "bg-blue-500",
      C: "bg-green-500",
      D: "bg-yellow-500"
    };

    return (
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <span className="bg-yellow-100 text-yellow-800 px-3 py-1 rounded-full text-sm font-semibold">
              Q{questionNumber}/{totalQuestions}
            </span>
            <span className="text-sm text-gray-500">
              {currentQuestion.type === "quiz" && "Knowledge Quiz"}
              {currentQuestion.type === "decision" && "Decision Scenario"}
              {currentQuestion.type === "simulation" && "Business Simulation"}
            </span>
          </div>
          <div className="flex items-center gap-2 text-gray-600">
            <Users className="w-5 h-5" />
            <span className="font-semibold">{responseCount}/{players.length}</span>
            <span className="text-sm">responded</span>
          </div>
        </div>

        {/* Question Card */}
        <div className="bg-gradient-to-br from-purple-600 to-purple-800 rounded-3xl p-8 mb-6 text-white">
          <div className="flex items-start gap-4">
            <div className="bg-white/20 p-3 rounded-xl">
              <Timer className="w-8 h-8" />
            </div>
            <div className="flex-1">
              <h2 className="text-2xl md:text-3xl font-bold leading-tight">
                {currentQuestion.question}
              </h2>
              {currentQuestion.time_limit && (
                <div className="mt-4 text-yellow-300 font-semibold">
                  ⏱️ {currentQuestion.time_limit} seconds
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Options */}
        {currentQuestion.options && (
          <div className="grid md:grid-cols-2 gap-4 mb-8">
            {currentQuestion.options.map((option) => {
              const count = answerDistribution[option.id] || 0;
              const total = Object.values(answerDistribution).reduce((a, b) => a + b, 0);
              const percentage = total > 0 ? Math.round((count / total) * 100) : 0;
              const isCorrect = currentQuestion.correct === option.id;
              
              return (
                <div
                  key={option.id}
                  className={`${optionColors[option.id]} rounded-2xl p-6 text-white relative overflow-hidden`}
                >
                  <div className="relative z-10">
                    <div className="text-4xl font-bold opacity-50 mb-2">{option.id}</div>
                    <div className="text-lg font-semibold">{option.text}</div>
                    
                    {showResults && (
                      <div className="mt-3 flex items-center gap-2">
                        <span className="text-2xl font-bold">{percentage}%</span>
                        <span className="text-sm opacity-75">({count} votes)</span>
                        {currentQuestion.type === "quiz" && isCorrect && (
                          <CheckCircle2 className="w-6 h-6 text-white" />
                        )}
                      </div>
                    )}
                  </div>
                  
                  {/* Progress bar for results */}
                  {showResults && (
                    <div 
                      className="absolute bottom-0 left-0 h-1 bg-white/30"
                      style={{ width: `${percentage}%` }}
                    />
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* Simulation Input Display */}
        {currentQuestion.type === "simulation" && currentQuestion.simulation_config && (
          <div className="bg-white rounded-2xl p-6 mb-8 shadow-lg">
            <p className="text-gray-600 text-center">
              Students are entering their responses...
            </p>
          </div>
        )}

        {/* Explanation (shown after results) */}
        {showResults && currentQuestion.explanation && (
          <div className="bg-green-50 border-2 border-green-200 rounded-2xl p-6 mb-8">
            <div className="flex items-start gap-3">
              <CheckCircle2 className="w-6 h-6 text-green-600 flex-shrink-0 mt-1" />
              <div>
                <h3 className="font-bold text-green-800 mb-1">Explanation</h3>
                <p className="text-green-700">{currentQuestion.explanation}</p>
              </div>
            </div>
          </div>
        )}

        {/* Leaderboard (shown after results) */}
        {showResults && leaderboard.length > 0 && (
          <div className="bg-white rounded-2xl p-6 mb-8 shadow-lg">
            <h3 className="font-bold text-gray-900 mb-4 flex items-center gap-2">
              <Trophy className="w-5 h-5 text-yellow-500" />
              Leaderboard
            </h3>
            <div className="space-y-2">
              {leaderboard.slice(0, 5).map((entry, idx) => (
                <div
                  key={idx}
                  className={`flex items-center justify-between p-3 rounded-lg ${
                    idx === 0 ? "bg-yellow-100" : "bg-gray-50"
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <span className={`w-8 h-8 rounded-full flex items-center justify-center font-bold ${
                      idx === 0 ? "bg-yellow-500 text-white" :
                      idx === 1 ? "bg-gray-400 text-white" :
                      idx === 2 ? "bg-amber-600 text-white" :
                      "bg-gray-200 text-gray-600"
                    }`}>
                      {entry.rank}
                    </span>
                    <span className="font-semibold">{entry.nickname}</span>
                  </div>
                  <span className="font-bold text-yellow-600">{entry.points} pts</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Control Buttons */}
        <div className="flex gap-4 justify-center">
          {!showResults ? (
            <button
              onClick={showQuestionResults}
              data-testid="show-results-btn"
              className="bg-blue-500 hover:bg-blue-600 text-white px-6 py-3 rounded-full font-semibold transition-all inline-flex items-center gap-2"
            >
              <BarChart3 className="w-5 h-5" />
              Show Results
            </button>
          ) : (
            <button
              onClick={nextQuestion}
              data-testid="next-question-btn"
              className="bg-yellow-500 hover:bg-yellow-600 text-white px-8 py-3 rounded-full font-bold transition-all inline-flex items-center gap-2"
            >
              <SkipForward className="w-5 h-5" />
              {questionNumber < totalQuestions ? "Next Question" : "Finish Game"}
            </button>
          )}
          <button
            onClick={endGame}
            className="bg-red-100 hover:bg-red-200 text-red-700 px-6 py-3 rounded-full font-semibold transition-all inline-flex items-center gap-2"
          >
            <StopCircle className="w-5 h-5" />
            End Game
          </button>
        </div>
      </div>
    );
  };

  // Game ended screen
  const renderGameEnded = () => (
    <div className="max-w-4xl mx-auto text-center">
      <div className="mb-8">
        <div className="text-6xl mb-4">🎉</div>
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Game Complete!</h1>
        <p className="text-gray-600">Great session! Here are the final results.</p>
      </div>

      {/* Final Leaderboard */}
      <div className="bg-white rounded-2xl p-8 shadow-lg mb-8">
        <h2 className="font-bold text-xl text-gray-900 mb-6 flex items-center justify-center gap-2">
          <Trophy className="w-6 h-6 text-yellow-500" />
          Final Standings
        </h2>
        <div className="space-y-3">
          {leaderboard.map((entry, idx) => (
            <div
              key={idx}
              className={`flex items-center justify-between p-4 rounded-xl ${
                idx === 0 ? "bg-gradient-to-r from-yellow-100 to-yellow-50 border-2 border-yellow-300" :
                idx === 1 ? "bg-gray-100" :
                idx === 2 ? "bg-amber-50" :
                "bg-gray-50"
              }`}
            >
              <div className="flex items-center gap-4">
                <span className={`w-10 h-10 rounded-full flex items-center justify-center font-bold text-lg ${
                  idx === 0 ? "bg-yellow-500 text-white" :
                  idx === 1 ? "bg-gray-400 text-white" :
                  idx === 2 ? "bg-amber-600 text-white" :
                  "bg-gray-300 text-gray-700"
                }`}>
                  {entry.rank}
                </span>
                <div className="text-left">
                  <span className="font-bold text-gray-900">{entry.nickname}</span>
                  {entry.disc_type && (
                    <span className="ml-2 text-sm text-gray-500">
                      ({entry.disc_type})
                    </span>
                  )}
                </div>
              </div>
              <span className="font-bold text-xl text-yellow-600">{entry.points} pts</span>
            </div>
          ))}
        </div>
      </div>

      <div className="flex gap-4 justify-center">
        <button
          onClick={resetGame}
          className="bg-yellow-500 hover:bg-yellow-600 text-white px-8 py-3 rounded-full font-bold transition-all inline-flex items-center gap-2"
        >
          <Play className="w-5 h-5" />
          Start New Game
        </button>
        <button
          onClick={() => navigate("/dashboard")}
          className="bg-gray-200 hover:bg-gray-300 text-gray-700 px-6 py-3 rounded-full font-semibold transition-all"
        >
          Back to Dashboard
        </button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-yellow-50 to-orange-50">
      <Navbar user={user} onLogout={onLogout} />
      
      <main className="max-w-7xl mx-auto px-4 py-8">
        {gameState === "idle" && renderModuleSelection()}
        {gameState === "waiting" && renderWaitingRoom()}
        {gameState === "active" && renderActiveQuestion()}
        {gameState === "ended" && renderGameEnded()}
      </main>
    </div>
  );
}

export default GameHostPage;
